import {
    useEffect, useMemo, useRef, useState,
} from 'react';
import {
    ListGroup, ListGroupItem,
} from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import cls from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBriefcase,
    faBuilding,
    faClock,
    faDiagramProject,
    faFileContract,
    faFileInvoice,
    faSearch,
    faTimes,
    faToolbox,
    faUser,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from '@s20/components/common/Link';
import { useDebouncedValue } from '@s20/hooks/useDebouncedValue';
import { getPermissionStrings } from '@s20/selectors/features/permissions.selectors';
import { Hide } from '@s20/components/common/Hide';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes, ApplicationPermissions } from '../Common';
import { IconButton, Input } from '../common/forms/FormElements';
import { Alert } from '../common/Modal';
import CustomCircularProgress from '../common/CustomCircularProgress';
import Chip from '../common/forms/Chip';
import { SectionHeader } from './SectionHeader';

const FILTER_TYPES = [
    {
        key: 'employee', label: 'employee:', example: 'employee:john smith', description: 'Search employees by name, number or email', icon: faUser, permission: ApplicationPermissions.employee_view,
    },
    {
        key: 'contract', label: 'contract:', example: 'contract:12345', description: 'Search contracts by number or description', icon: faFileContract, permission: ApplicationPermissions.contract_view,
    },
    {
        key: 'job', label: 'job:', example: 'job:main street', description: 'Search jobs by location or number', icon: faBriefcase, permission: ApplicationPermissions.job_view,
    },
    {
        key: 'project', label: 'project:', example: 'project:main street', description: 'Search projects by name, location or work order', icon: faDiagramProject, permission: ApplicationPermissions.job_view,
    },
    {
        key: 'org', label: 'org:', example: 'org:acme corp', description: 'Search for organizations by name', icon: faBuilding, permission: ApplicationPermissions.company_view,
    },
    {
        key: 'equipment', label: 'equipment:', example: 'equipment:truck-01', description: 'Search for equipment by description or number', icon: faToolbox, permission: ApplicationPermissions.equipment_view,
    },
    {
        key: 'timesheet', label: 'timesheet:', example: 'timesheet:2026-1-26-4', description: 'Search for timesheet by timesheet number', icon: faClock, permission: ApplicationPermissions.timesheet_view,
    },
    {
        key: 'invoice', label: 'invoice:', example: 'invoice:12345', description: 'Search for invoices by number or timesheet', icon: faFileInvoice, permission: ApplicationPermissions.invoice_view,
    },
];

const TYPE_ICON = {
    employee: faUser,
    contract: faFileContract,
    job: faBriefcase,
    project: faDiagramProject,
    org: faBuilding,
    equipment: faToolbox,
    timesheet: faClock,
    invoice: faFileInvoice,
};

const parseQuery = (raw, availableFilters) => {
    const match = raw.match(/^(\w+):/);
    if (match) {
        const filter = availableFilters.find((f) => f.key === match[1].toLowerCase());
        if (filter) {
            return { filterKey: filter.key, term: raw.slice(match[0].length).trim() };
        }
    }
    return { filterKey: null, term: raw.trim() };
};

const flattenResults = (response) => {
    if (!response) return [];
    return [
        ...(response.employees || []),
        ...(response.contracts || []),
        ...(response.jobs || []),
        ...(response.projects || []),
        ...(response.organizations || []),
        ...(response.equipment || []),
        ...(response.timesheets || []),
        ...(response.invoices || []),
    ];
};

const MIN_SEARCH_LENGTH = 3;

export const QuickSearchBody = () => {
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const history = useHistory();

    const permissionStrings = useSelector(getPermissionStrings);
    const availableFilters = useMemo(
        () => FILTER_TYPES.filter((f) => permissionStrings.includes(f.permission)),
        [permissionStrings],
    );

    const { filterKey, term } = useMemo(
        () => parseQuery(query, availableFilters),
        [query, availableFilters],
    );
    const debouncedQuery = useDebouncedValue(query, 300);
    const { filterKey: fetchFilterKey, term: fetchTerm } = useMemo(
        () => parseQuery(debouncedQuery, availableFilters),
        [debouncedQuery, availableFilters],
    );

    const { data, isFetching } = useQuery({
        queryKey: ['quickSearch', fetchTerm, fetchFilterKey],
        queryFn: () => httpClient.post(ApiRoutes.quickSearch.search(), {
            query: fetchTerm,
            filterType: fetchFilterKey,
        }),
        enabled: Boolean(fetchFilterKey) && fetchTerm.length >= MIN_SEARCH_LENGTH,
    });

    const results = useMemo(() => flattenResults(data), [data]);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        setSelectedIndex(0);
    }, [term, filterKey]);

    useEffect(() => {
        const item = scrollContainerRef.current?.querySelector(
            `[data-qs-result-index="${selectedIndex}"]`,
        );
        if (!item) return;

        let scrollEl = item.parentElement;
        while (scrollEl && scrollEl !== document.body) {
            const { overflowY } = window.getComputedStyle(scrollEl);
            if ((overflowY === 'auto' || overflowY === 'scroll')
                && scrollEl.scrollHeight > scrollEl.clientHeight) {
                break;
            }
            scrollEl = scrollEl.parentElement;
        }
        if (!scrollEl || scrollEl === document.body) return;

        const scrollRect = scrollEl.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();

        if (itemRect.bottom > scrollRect.bottom) {
            scrollEl.scrollTop += itemRect.bottom - scrollRect.bottom;
        } else if (itemRect.top < scrollRect.top) {
            scrollEl.scrollTop -= scrollRect.top - itemRect.top;
        }
    }, [selectedIndex, results]);

    const selectResult = (result) => {
        if (!result) return;
        Alert.hide();
        history.push(result.path);
    };

    const handleKeyDown = (e) => {
        if (!results.length) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            selectResult(results[selectedIndex]);
        }
    };

    const isPendingDebounce = query !== debouncedQuery;
    const hasMinChars = term.length >= MIN_SEARCH_LENGTH;
    const showSuggestions = !filterKey;
    const showMinCharsHint = Boolean(filterKey) && term.length > 0 && !hasMinChars;
    const showLoading = Boolean(filterKey) && hasMinChars && (isFetching || isPendingDebounce);
    const showResults = Boolean(filterKey) && hasMinChars && !showLoading && results.length > 0;
    const showEmpty = Boolean(filterKey) && hasMinChars && !showLoading && results.length === 0;

    const activeFilter = filterKey ? availableFilters.find((f) => f.key === filterKey) : null;
    const placeholder = activeFilter
        ? activeFilter.description
        : 'Select a filter below to begin searching...';

    return (
        <div className="d-flex flex-column h-100">
            <div className="flex-none d-flex align-items-center">
                <Input
                    type="search"
                    name="quickSearchQuery"
                    size="lg"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    autoComplete="off"
                    innerRef={inputRef}
                    formGroupClassName="flex-fill"
                    startAddonClassName="addon-centered"
                    startAddon={(
                        <FontAwesomeIcon
                            icon={faSearch}
                            size="lg"
                            className="text-muted"
                        />
                    )}
                />

                <Hide smDown>
                    <IconButton
                        className="ml-4"
                        onClick={Alert.hide}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                </Hide>
            </div>

            <div
                ref={scrollContainerRef}
                className="mt-3 flex-fill overflow-auto"
            >
                {showSuggestions && (
                    <div className="mb-3">
                        <SectionHeader title="Suggested filters" />
                        <ListGroup flush>
                            {availableFilters.map((filter) => (
                                <ListGroupItem
                                    key={filter.key}
                                    tag="button"
                                    type="button"
                                    action
                                    className="d-flex align-items-center border-0 px-3 py-2"
                                    onClick={() => {
                                        setQuery(`${filter.key}:`);
                                        inputRef.current?.focus();
                                    }}
                                >
                                    <Chip
                                        text={filter.label}
                                        color="light"
                                        className="mr-3 shadow-sm"
                                    />
                                    <span className="text-muted small flex-grow-1 text-truncate">
                                        {filter.example}
                                    </span>
                                    <span className="text-muted small ml-3 text-right d-none d-md-inline">
                                        {filter.description}
                                    </span>
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    </div>
                )}

                {showMinCharsHint && (
                    <div className="text-center text-muted small py-4">
                        Type at least {MIN_SEARCH_LENGTH} characters to search
                    </div>
                )}

                {showLoading && (
                    <div className="py-4">
                        <CustomCircularProgress
                            size="sm"
                            label="Searching..."
                        />
                    </div>
                )}

                {showResults && (
                    <div className="mb-2">
                        <SectionHeader
                            title="Results"
                            count={results.length}
                        />

                        <ListGroup
                            flush
                            style={{ maxHeight: '60dvh' }}
                        >
                            {results.map((result, index) => (
                                <Link
                                    key={`${result.type}-${result.id}`}
                                    to={result.path}
                                    className={cls('d-flex align-items-center px-3 py-2 list-group-item list-group-item-action', {
                                        active: index === selectedIndex,
                                    })}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        Alert.hide();
                                    }}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                    data-qs-result-index={index}
                                >
                                    <FontAwesomeIcon
                                        icon={TYPE_ICON[result.type]}
                                        className="mr-3 text-muted"
                                        fixedWidth
                                    />

                                    <div className="flex-grow-1 text-truncate">
                                        <div className="font-weight-bold text-truncate">
                                            {result.title}
                                        </div>
                                        <div className="small text-muted text-truncate">
                                            {result.subtitle}
                                            {Boolean(result.subtitle && result.status) && ' · '}
                                            {result.status}
                                        </div>
                                    </div>

                                    <Chip
                                        text={result.type}
                                        color="light"
                                        className="ml-3 shadow-sm text-uppercase small text-small font-weight-bold"
                                    />
                                </Link>
                            ))}
                        </ListGroup>
                    </div>
                )}

                {showEmpty && (
                    <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                        <FontAwesomeIcon
                            icon={faSearch}
                            size="2x"
                            className="text-muted mb-3"
                        />

                        <div className="font-weight-bold text-secondary mb-1">
                            No results found for &ldquo;{query}&rdquo;
                        </div>

                        <div className="small text-muted">
                            Try a different search term or filter
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-3 pt-3 border-top d-flex justify-content-center align-items-center small flex-none">
                <span className="mr-4">
                    <kbd className="mr-1 shadow-sm">&#8593;&#8595;</kbd>
                    {' '}
                    to navigate
                </span>

                <span className="mr-4">
                    <kbd className="mr-1 shadow-sm">&#8629;</kbd>
                    {' '}
                    to select
                </span>

                <span>
                    <kbd className="mr-1 shadow-sm">esc</kbd>
                    {' '}
                    to close
                </span>
            </div>
        </div>
    );
};

export default QuickSearchBody;
